import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileText, Search, Users, X } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface ReportCustomer {
  name: string;
  customer_name: string;
  mobile?: string;
}

interface PaymentBreakdownRow {
  payment_method: string;
  amount: number;
  count: number;
}

interface SaleSummary {
  total_invoices: number;
  gross_sales: number;
  discount: number;
  write_off?: number;
  tax: number;
  net_sales: number;
  refunds: number;
  final_sales: number;
}

export default function SaleSummarySection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [cashier, setCashier] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  const { data: cashiers = [] } = useQuery({
    queryKey: ['reportCashiers'],
    queryFn: () => reportsApi.getReportCashiers(),
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['reportPaymentMethods'],
    queryFn: () => reportsApi.getReportPaymentMethods(),
  });

  const { data: customersRes } = useQuery({
    queryKey: ['customersForSaleSummary', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 50 });
      return res.data.message;
    },
  });

  const customers: ReportCustomer[] = customersRes?.customers || customersRes || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: ReportCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['saleSummary', from_date, to_date, cashier, selectedCustomer, paymentMethod],
    queryFn: () =>
      reportsApi.getSaleSummary({
        from_date,
        to_date,
        cashier: cashier || undefined,
        customer: selectedCustomer || undefined,
        payment_method: paymentMethod || undefined,
      }),
  });

  const summary: SaleSummary | undefined = data?.summary;
  const breakdown: PaymentBreakdownRow[] = data?.payment_breakdown || [];

  const kpiCards = summary
    ? [
        { label: 'Total Invoices', value: String(summary.total_invoices) },
        { label: 'Gross Sales', value: `${formatMoney(summary.gross_sales)}` },
        { label: 'Discount', value: `${formatMoney(summary.discount)}` },
        { label: 'Write-off', value: `${formatMoney(summary.write_off || 0)}` },
        { label: 'Tax', value: `${formatMoney(summary.tax)}` },
        { label: 'Net Sales', value: `${formatMoney(summary.net_sales)}` },
        { label: 'Refunds', value: `${formatMoney(summary.refunds)}` },
        { label: 'Final Sales', value: `${formatMoney(summary.final_sales)}` },
      ]
    : [];

  const handleExport = () => {
    if (!summary) return;
    downloadCsv(
      `sale-summary-${from_date}-${to_date}.csv`,
      [
        { key: 'total_invoices', label: 'Total Invoices' },
        { key: 'gross_sales', label: 'Gross Sales' },
        { key: 'discount', label: 'Discount' },
        { key: 'write_off', label: 'Write-off' },
        { key: 'tax', label: 'Tax' },
        { key: 'net_sales', label: 'Net Sales' },
        { key: 'refunds', label: 'Refunds' },
        { key: 'final_sales', label: 'Final Sales' },
      ],
      [summary as unknown as Record<string, unknown>]
    );
    if (breakdown.length > 0) {
      downloadCsv(
        `sale-summary-payments-${from_date}-${to_date}.csv`,
        [
          { key: 'payment_method', label: 'Payment Method' },
          { key: 'amount', label: 'Amount' },
          { key: 'count', label: 'Count' },
        ],
        breakdown as unknown as Record<string, unknown>[]
      );
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Cashier</label>
            <select className="input-field" value={cashier} onChange={(e) => setCashier(e.target.value)}>
              <option value="">All Cashiers</option>
              {(cashiers as { user: string; full_name: string }[]).map((c) => (
                <option key={c.user} value={c.user}>
                  {c.full_name || c.user}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              Customer
            </label>
            {selectedCustomer ? (
              <div className="input-field flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-gray-900">{selectedCustomerLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelectCustomer()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  aria-label="Clear customer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder={t('reports.searchByNamePhone')}
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer()}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                    >
                      All Customers
                    </button>
                    {customers.length > 0 ? (
                      customers.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50"
                        >
                          <p className="font-medium text-gray-900">{c.customer_name}</p>
                          {c.mobile && <p className="text-xs text-gray-500">{c.mobile}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">No customers found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Payment Method</label>
            <select
              className="input-field"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">All Methods</option>
              {(paymentMethods as string[]).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <ReportExportButton onClick={handleExport} disabled={!summary} />
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : summary ? (
        <>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {kpiCards.map((kpi) => (
              <div key={kpi.label} className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                <p className="mt-1 text-base font-semibold text-gray-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto border-t border-gray-100">
            {breakdown.length > 0 ? (
              <table className="w-full text-sm text-left min-w-[360px]">
                <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 font-medium">Payment Method</th>
                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                    <th className="px-4 py-3 font-medium text-right">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {breakdown.map((row) => (
                    <tr key={row.payment_method} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-gray-900">{row.payment_method}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatMoney(row.amount)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-sm text-gray-400">No payment breakdown</div>
            )}
          </div>
        </>
      ) : (
        <div className="p-12 text-center">
          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No summary data for the selected filters</p>
        </div>
      )}
    </div>
  );
}
